import { c as _c } from "react/compiler-runtime";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import * as React from "react";
import cn from "twsx";
const toneClassName = {
  accent: "bg-accent",
  info: "bg-blue-strong",
  warning: "bg-yellow-light",
  success: "bg-green-light",
  danger: "bg-red-light"
} as const;
export type ProgressProps = React.ComponentProps<typeof ProgressPrimitive.Root> & {
  barClassName?: string;
  tone?: keyof typeof toneClassName;
  size?: "sm" | "md";
  marker?: number;
};

// `size="sm"` is the 4px meter used inside cards and tables; `tone` names the
// state (on track, behind) instead of a call-site colour.
// `marker` draws a tick at a percentage, such as where a schedule expects
// progress to be today.
/** Use when showing how far along a task or quota is. */
const Progress = t0 => {
  const $ = _c(32);
  let barClassName;
  let className;
  let marker;
  let props;
  let ref;
  let t1;
  let t2;
  let value;
  if ($[0] !== t0) {
    ({
      className,
      value,
      barClassName,
      tone: t1,
      size: t2,
      marker,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = barClassName;
    $[2] = className;
    $[3] = marker;
    $[4] = props;
    $[5] = ref;
    $[6] = t1;
    $[7] = t2;
    $[8] = value;
  } else {
    barClassName = $[1];
    className = $[2];
    marker = $[3];
    props = $[4];
    ref = $[5];
    t1 = $[6];
    t2 = $[7];
    value = $[8];
  }
  const tone = t1 === undefined ? "accent" : t1;
  const size = t2 === undefined ? "md" : t2;
  const t3 = marker === undefined && "overflow-hidden";
  const t4 = size === "sm" ? "h-1" : "h-2";
  let t5;
  if ($[9] !== className || $[10] !== t3 || $[11] !== t4) {
    t5 = cn("relative w-full rounded-full bg-fill-hover", t3, t4, className);
    $[9] = className;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = toneClassName[tone];
  const t7 = marker !== undefined && "rounded-full";
  let t8;
  if ($[13] !== barClassName || $[14] !== t6 || $[15] !== t7) {
    t8 = cn("h-full w-full flex-1 transition-transform duration-normal ease-standard motion-reduce:transition-none", t6, t7, barClassName);
    $[13] = barClassName;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== marker || $[18] !== value) {
    t9 = marker === undefined ? {
      transform: `translateX(-${100 - Math.min(100, Math.max(0, value || 0))}%)`
    } : {
      width: `${Math.min(100, Math.max(0, value || 0))}%`
    };
    $[17] = marker;
    $[18] = value;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = <ProgressPrimitive.Indicator className={t8} style={t9} />;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== marker) {
    t11 = marker !== undefined && <span aria-hidden="true" className="absolute -inset-y-1 w-0.5 -translate-x-1/2 rounded-full bg-text-primary" style={{
      left: `${Math.min(100, Math.max(0, marker))}%`
    }} />;
    $[23] = marker;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== props || $[26] !== ref || $[27] !== t10 || $[28] !== t11 || $[29] !== t5 || $[30] !== value) {
    t12 = <ProgressPrimitive.Root ref={ref} value={value} className={t5} {...props}>{t10}{t11}</ProgressPrimitive.Root>;
    $[25] = props;
    $[26] = ref;
    $[27] = t10;
    $[28] = t11;
    $[29] = t5;
    $[30] = value;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
};
Progress.displayName = ProgressPrimitive.Root.displayName;
export { Progress };